import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { enqueueSnackbar } from "notistack";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  getNamingConfigs,
  createNamingConfig,
  updateNamingConfig,
  deleteNamingConfig,
  getMatchingAssetsForNaming,
  getNamingInstitutionOptions,
  BARCODE_CATEGORY_OPTIONS,
} from "../../services/BarcodeService";
import {
  getAllAstInstitutions,
  getAstInstitutionCategories,
  getAstInstitutionsByCategory,
} from "../../services/ValitanApiService";

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const getInstitutionName = (item) =>
  String(item?.institution_name || item?.institutionName || item?.name || "").trim();

const getWardId = (item) =>
  String(item?.ward_id || item?.wardId || item?.ward?.id || "").trim();

const getWardName = (item) =>
  String(item?.ward_name || item?.wardName || item?.ward?.name || "").trim();

const getInstitutionFilters = (item) => {
  const rawFilters = Array.isArray(item?.institution_filters)
    ? item.institution_filters
    : Array.isArray(item?.institutionFilters)
      ? item.institutionFilters
      : [];

  if (rawFilters.length > 0) {
    return rawFilters
      .map((filter) => ({
        name: String(filter?.name || "").trim(),
        wardId: String(filter?.ward_id || filter?.wardId || "").trim(),
        wardName: String(filter?.ward_name || filter?.wardName || "").trim(),
      }))
      .filter((filter) => filter.name && filter.wardId);
  }

  const fallbackName = getInstitutionName(item);
  const fallbackWardId = getWardId(item);
  const fallbackWardName = getWardName(item);
  if (!fallbackName) return [];
  return [{ name: fallbackName, wardId: fallbackWardId, wardName: fallbackWardName }];
};

const formatInstitutionWardName = (name, wardName) => {
  const normalizedName = String(name || "").trim();
  const normalizedWard = String(wardName || "").trim();
  if (!normalizedWard || normalizedWard === "Unknown") return normalizedName;
  return `${normalizedName}-${normalizedWard}`;
};

const normalizeCompareValue = (value) =>
  String(value || "").trim().toLowerCase();

const getAssetInstitutionName = (asset) =>
  String(asset?.institution_name || asset?.institutionName || asset?.institution?.name || "").trim();

const getAssetWardId = (asset) =>
  String(asset?.ward_id || asset?.wardId || asset?.ward?.id || "").trim();

const getAssetWardName = (asset) =>
  String(asset?.ward_name || asset?.wardName || asset?.ward?.name || "").trim();

const buildInstitutionFilters = (institutions) =>
  institutions.map((institution) => ({
    name: String(institution.name || "").trim(),
    wardId: String(institution.wardId || "").trim(),
    wardName: String(institution.wardName || "").trim(),
  })).filter((institution) => institution.name && institution.wardId);

const serializeInstitutionFilters = (institutionFilters) =>
  institutionFilters.map((institution) => `${institution.name} [${institution.wardId}]`).join(",");

const assetMatchesInstitutionFilters = (asset, institutionFilters) => {
  if (!institutionFilters.length) return true;

  const assetInstitutionName = normalizeCompareValue(getAssetInstitutionName(asset));
  const assetWardId = normalizeCompareValue(getAssetWardId(asset));
  const assetWardName = normalizeCompareValue(getAssetWardName(asset));

  return institutionFilters.some((filter) => {
    const filterName = normalizeCompareValue(filter.name);
    const filterWardId = normalizeCompareValue(filter.wardId);
    const filterWardName = normalizeCompareValue(filter.wardName);

    const nameMatches = !assetInstitutionName || assetInstitutionName === filterName;
    const wardMatches =
      filterWardId
        ? assetWardId === filterWardId || (!assetWardId && Boolean(filterWardName) && assetWardName === filterWardName)
        : !filterWardName || assetWardName === filterWardName;

    return nameMatches && wardMatches;
  });
};

const institutionSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    alignItems: "flex-start",
  }),
  valueContainer: (base) => ({
    ...base,
    alignItems: "flex-start",
    maxHeight: 130,
    overflowY: "auto",
    paddingTop: 6,
    paddingBottom: 6,
  }),
  multiValue: (base) => ({
    ...base,
    maxWidth: "100%",
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
  }),
};

const BarcodeNamingConfig = () => {
  document.title = "Barcode Naming Config | Valitan";

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [instCategories, setInstCategories] = useState([]);
  const [selectedInstCategory, setSelectedInstCategory] = useState("");
  const [matchingAssets, setMatchingAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  const [form, setForm] = useState({
    institutions: [],
    asset_category_code: "FURN",
    location_short_name: "",
    institution_short_name: "",
    asset_short_name: "",
    asset_name_pattern: "",
    description: "",
    asset_ids: "",
  });

  const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());
  const [configSearch, setConfigSearch] = useState("");
  const [institutionSearchInput, setInstitutionSearchInput] = useState("");
  const [assetInstitutionOptions, setAssetInstitutionOptions] = useState([]);

  const filteredConfigs = useMemo(() => {
    if (!configSearch) return configs;
    const s = configSearch.toLowerCase();
    return configs.filter(c => 
      (c.institution_name || c.institutionName || "").toLowerCase().includes(s) ||
      (c.asset_category_code || c.assetCategoryCode || "").toLowerCase().includes(s) ||
      (c.description || "").toLowerCase().includes(s) ||
      (c.location_short_name || c.locationShortName || "").toLowerCase().includes(s) ||
      (c.institution_short_name || c.institutionShortName || "").toLowerCase().includes(s)
    );
  }, [configs, configSearch]);

  const filteredAssets = matchingAssets;

  const institutionOptions = useMemo(() => {
    const typed = institutionSearchInput.trim();
    const sourceOptions = typed ? assetInstitutionOptions : institutions;
    if (!typed) return sourceOptions;
    if (sourceOptions.length > 0) return sourceOptions;
    return sourceOptions;
  }, [assetInstitutionOptions, institutionSearchInput, institutions]);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await getNamingConfigs();
      setConfigs(response.data.data || []);
    } catch (error) {
      enqueueSnackbar("Failed to load naming configurations", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadInstCategories = async () => {
    try {
      const response = await getAstInstitutionCategories();
      setInstCategories(parseList(response));
    } catch (error) {
      console.error("Failed to load institutional categories", error);
    }
  };

  const loadInstitutions = async (categoryId = "") => {
    try {
      let response;
      if (categoryId) {
        response = await getAstInstitutionsByCategory(categoryId);
      } else {
        response = await getAllAstInstitutions();
      }
      const content = Array.isArray(response) ? response : parseList(response);
      setInstitutions(content.map(i => {
        const name = getInstitutionName(i);
        const wardId = getWardId(i);
        const wardName = getWardName(i);
        return {
          value: `${name}|${wardId}`,
          label: formatInstitutionWardName(name, wardName),
          name,
          wardId,
          wardName
        };
      }));
    } catch (error) {
      console.error("Failed to load institutions", error);
    }
  };

  const loadAssetInstitutionOptions = useCallback(async (searchText, categoryCode) => {
    const normalizedSearch = String(searchText || "").trim();
    if (normalizedSearch.length < 2) {
      setAssetInstitutionOptions([]);
      return;
    }

    try {
      const response = await getNamingInstitutionOptions(normalizedSearch, categoryCode);
      const rows = response?.data?.data || [];
      setAssetInstitutionOptions(rows.map((row) => {
        const name = getInstitutionName(row);
        const wardId = getWardId(row);
        const wardName = getWardName(row);
        const assetCount = row.asset_count || row.assetCount || 0;
        return {
          value: `${name}|${wardId}`,
          label: `${formatInstitutionWardName(name, wardName)}${assetCount ? ` (${assetCount})` : ""}`,
          name,
          wardId,
          wardName,
          assetCount,
        };
      }));
    } catch (error) {
      console.error("Failed to load asset-backed institution options", error);
      setAssetInstitutionOptions([]);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
    loadInstitutions();
    loadInstCategories();
  }, []);

  const fetchMatchingAssets = useCallback(async (
    selectedInstitutions,
    catCode,
    wardId = null
  ) => {
    const institutionFilters = Array.isArray(selectedInstitutions)
      ? buildInstitutionFilters(selectedInstitutions)
      : buildInstitutionFilters([{ name: selectedInstitutions, wardId }]);

    if (!institutionFilters.length) {
      setMatchingAssets([]);
      return;
    }
    setLoadingAssets(true);
    try {
      const responses = await Promise.all(
        institutionFilters.map((institution) =>
          getMatchingAssetsForNaming(
            institution.name,
            catCode,
            institution.wardId,
            ""
          )
        )
      );

      const mergedRows = responses.flatMap((response) => {
        const rows = response?.data?.data;
        return Array.isArray(rows) ? rows : [];
      });

      const dedupedRows = Array.from(
        new Map(
          mergedRows.map((asset) => [
            String(asset?.id || `${getAssetInstitutionName(asset)}|${getAssetWardId(asset)}|${asset?.name || ""}`),
            asset,
          ])
        ).values()
      );

      setMatchingAssets(
        dedupedRows.filter((asset) => assetMatchesInstitutionFilters(asset, institutionFilters))
      );
    } catch (error) {
      console.error("Failed to fetch matching assets", error);
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  // Handle debounced server-side search
  useEffect(() => {
    if (!modalOpen) return;

    const instNames = form.institutions.length > 0 
      ? form.institutions
      : [{ name: institutionSearchInput.trim(), wardId: "", wardName: "" }];

    if (!buildInstitutionFilters(instNames).length) {
      setMatchingAssets([]);
      return;
    }
    
    const delayDebounce = setTimeout(() => {
      fetchMatchingAssets(
        instNames,
        form.asset_category_code
      );
    }, 400);

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionSearchInput, form.institutions, form.asset_category_code, modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;

    const delayDebounce = setTimeout(() => {
      loadAssetInstitutionOptions(institutionSearchInput, form.asset_category_code);
    }, 250);

    return () => clearTimeout(delayDebounce);
  }, [form.asset_category_code, institutionSearchInput, loadAssetInstitutionOptions, modalOpen]);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setEditingId(null);
      setForm({
        institutions: [],
        asset_category_code: "FURN",
        location_short_name: "",
        institution_short_name: "",
        asset_short_name: "",
        asset_name_pattern: "",
        description: "",
        asset_ids: "",
      });
      setSelectedInstCategory("");
      setInstitutionSearchInput("");
      setAssetInstitutionOptions([]);
      loadInstitutions();
      setMatchingAssets([]);
      setSelectedAssetIds(new Set());
    }
  };

  const handleEdit = (config) => {
    const institutionFilters = getInstitutionFilters(config);
    setEditingId(config.id);
    setForm({
      institutions: institutionFilters,
      asset_category_code: config.asset_category_code || config.assetCategoryCode,
      location_short_name: config.location_short_name || config.locationShortName || "",
      institution_short_name: config.institution_short_name || config.institutionShortName || "",
      asset_short_name: config.asset_short_name || config.assetShortName || "",
      asset_name_pattern: config.asset_name_pattern || config.assetNamePattern || "",
      description: config.description || "",
      asset_ids: config.asset_ids || config.assetIds || "",
    });
    setModalOpen(true);
    const catCode = config.asset_category_code || config.assetCategoryCode;
    const idsString = config.asset_ids || config.assetIds || "";
    fetchMatchingAssets(institutionFilters, catCode);
    const ids = idsString ? new Set(idsString.split(",")) : new Set();
    setSelectedAssetIds(ids);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this configuration?")) return;
    try {
      await deleteNamingConfig(id);
      enqueueSnackbar("Configuration deleted", { variant: "success" });
      loadConfigs();
    } catch (error) {
      enqueueSnackbar("Failed to delete configuration", { variant: "error" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const institutionFilters = buildInstitutionFilters(form.institutions);
    if (institutionFilters.length === 0) {
      enqueueSnackbar("Please select at least one institution with a ward.", { variant: "warning" });
      return;
    }

    try {
      const finalForm = {
        ...form,
        institution_name: serializeInstitutionFilters(institutionFilters),
        institution_filters: institutionFilters.map((institution) => ({
          name: institution.name,
          ward_id: institution.wardId,
          ward_name: institution.wardName,
        })),
        asset_ids: Array.from(selectedAssetIds).join(","),
      };
      delete finalForm.institutions;

      if (editingId) {
        await updateNamingConfig(editingId, finalForm);
        enqueueSnackbar("Configuration updated", { variant: "success" });
      } else {
        await createNamingConfig(finalForm);
        enqueueSnackbar("Configuration created", { variant: "success" });
      }
      toggleModal();
      loadConfigs();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || "Failed to save configuration(s)", { variant: "error" });
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Barcodes" breadcrumbItem="Naming Configurations" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">Custom Barcode Naming Rules</h4>
                  <div className="d-flex align-items-center">
                    <div className="search-box me-2 d-inline-block">
                      <Input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search configs..."
                        value={configSearch}
                        onChange={(e) => setConfigSearch(e.target.value)}
                        style={{ width: "250px" }}
                      />
                    </div>
                    <Button color="primary" onClick={toggleModal}>
                      <i className="ri-add-line align-bottom me-1"></i> Add Configuration
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Institution</th>
                        <th>Category</th>
                        <th>Loc. Short</th>
                        <th>Inst. Short</th>
                        <th>Asset Short</th>
                        <th>Description</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center"><Spinner size="sm" /></td>
                        </tr>
                      ) : filteredConfigs.length > 0 ? (
                        filteredConfigs.map((config) => (
                          <tr key={config.id}>
                            <td>
                              {(() => {
                                const institutionFilters = getInstitutionFilters(config);
                                return institutionFilters
                                  .map((institution) => {
                                    const match = institutions.find(i =>
                                      i.name === institution.name && String(i.wardId || "") === String(institution.wardId || "")
                                    );
                                    const wardName = institution.wardName || match?.wardName || "";
                                    return formatInstitutionWardName(institution.name, wardName);
                                  })
                                  .join(", ");
                              })()}
                            </td>
                            <td>
                              {(config.asset_category_code || config.assetCategoryCode) ? (
                                <Badge color="info">{config.asset_category_code || config.assetCategoryCode}</Badge>
                              ) : (
                                <Badge color="secondary">All Categories</Badge>
                              )}
                            </td>
                            <td><code>{config.location_short_name || config.locationShortName}</code></td>
                            <td><code>{config.institution_short_name || config.institutionShortName}</code></td>
                            <td><code>{config.asset_short_name || config.assetShortName}</code></td>
                            <td>
                              {config.description}
                              {(config.asset_name_pattern || config.assetNamePattern) && (
                                <div className="text-primary small mt-1">
                                  Matches names containing: <strong>{config.asset_name_pattern || config.assetNamePattern}</strong>
                                </div>
                              )}
                              {(config.asset_ids || config.assetIds) && (
                                <div className="text-muted small mt-1">
                                  Applies to {(config.asset_ids || config.assetIds).split(",").length} specific assets
                                </div>
                              )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <div className="small text-muted" title="Updated At">
                                <i className="ri-edit-2-line me-1"></i>
                                {config.updated_at ? new Date(config.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-"}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-nowrap gap-2">
                                <Button color="primary" size="sm" onClick={() => handleEdit(config)}>
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button color="danger" size="sm" onClick={() => handleDelete(config.id)}>
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center">No configurations found</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          {editingId ? "Edit Configuration" : "Add Configuration"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Institutional Category</Label>
                  <Input
                    type="select"
                    value={selectedInstCategory}
                    onChange={(e) => {
                      const catId = e.target.value;
                      setSelectedInstCategory(catId);
                      loadInstitutions(catId);
                    }}
                  >
                    <option value="">-- All Categories --</option>
                    {instCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Category</Label>
                  <Input
                    type="select"
                    value={form.asset_category_code}
                    onChange={(e) => {
                      setForm({ ...form, asset_category_code: e.target.value });
                      fetchMatchingAssets(form.institutions, e.target.value);
                    }}
                  >
                    <option value="">-- All Categories --</option>
                    {BARCODE_CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.code} value={opt.code}>{opt.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>Institution</Label>
                  <Select
                    isMulti={!editingId}
                    options={institutionOptions}
                    value={
                      form.institutions.map(inst => 
                        institutionOptions.find(opt => opt.name === inst.name && String(opt.wardId || "") === String(inst.wardId || "")) 
                        || {
                          value: `${inst.name}|${inst.wardId || ""}`,
                          label: formatInstitutionWardName(inst.name, inst.wardName),
                          name: inst.name,
                          wardId: inst.wardId || "",
                          wardName: inst.wardName || ""
                        }
                      )
                    }
                    inputValue={institutionSearchInput}
                    onInputChange={(value, actionMeta) => {
                      if (actionMeta.action === "input-change") {
                        setInstitutionSearchInput(value);
                      }
                      if (actionMeta.action === "menu-close" && form.institutions.length > 0) {
                        setInstitutionSearchInput("");
                      }
                    }}
                    onChange={(selected) => {
                      if (selected && (!Array.isArray(selected) || selected.length > 0)) {
                        const selectedArray = Array.isArray(selected) ? selected : [selected];
                        const newInstitutions = selectedArray.map(item => ({
                          name: item.name,
                          wardId: item.wardId || "",
                          wardName: item.wardName || ""
                        }));
                        
                        setForm({ ...form, institutions: newInstitutions });
                        setInstitutionSearchInput("");
                        
                        fetchMatchingAssets(newInstitutions, form.asset_category_code);
                      } else {
                        setForm({ ...form, institutions: [] });
                        setInstitutionSearchInput("");
                        fetchMatchingAssets([], form.asset_category_code);
                      }
                    }}
                    placeholder={!editingId ? "Select Institutions" : "Select Institution"}
                    isClearable
                    styles={institutionSelectStyles}
                  />
                  {form.institutions.length > 0 && (
                    <Alert color="info" className="mt-2 mb-0 py-2">
                          {editingId ? (
                            <>
                          Saving configuration for{" "}
                          <strong>
                            {form.institutions
                              .map((inst) => formatInstitutionWardName(inst.name, inst.wardName))
                              .join(", ")}
                          </strong>.
                            </>
                          ) : (
                            <>
                          {form.institutions.length} institution{form.institutions.length === 1 ? "" : "s"} selected.
                          Saving will store them in one configuration using the same category and short names:{" "}
                          <strong>
                            {form.institutions
                              .map((inst) => formatInstitutionWardName(inst.name, inst.wardName))
                              .join(", ")}
                          </strong>
                        </>
                      )}
                    </Alert>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Location Short (e.g., BRKE)</Label>
                  <Input
                    type="text"
                    value={form.location_short_name}
                    onChange={(e) => setForm({ ...form, location_short_name: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Inst. Short (e.g., PS)</Label>
                  <Input
                    type="text"
                    value={form.institution_short_name}
                    onChange={(e) => setForm({ ...form, institution_short_name: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Asset Short (e.g., TAB)</Label>
                  <Input
                    type="text"
                    value={form.asset_short_name}
                    onChange={(e) => setForm({ ...form, asset_short_name: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Asset Name Pattern</Label>
                  <Input
                    type="text"
                    value={form.asset_name_pattern}
                    onChange={(e) => setForm({ ...form, asset_name_pattern: e.target.value })}
                    placeholder="e.g. TABLE"
                  />
                  <div className="text-muted small">Matches assets with this text in their name.</div>
                </FormGroup>
              </Col>
            </Row>

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">
                Matching Assets ({matchingAssets.length})
              </h5>
              <div className="d-flex align-items-center">
                <Button 
                  color="link" 
                  size="sm" 
                  className="me-2 text-decoration-none"
                  onClick={() => {
                    const newSet = new Set(selectedAssetIds);
                    filteredAssets.forEach(a => newSet.add(String(a.id)));
                    setSelectedAssetIds(newSet);
                  }}
                >
                  Select All
                </Button>
                <Button 
                  color="link" 
                  size="sm" 
                  className="me-2 text-decoration-none text-danger"
                  onClick={() => {
                    const newSet = new Set(selectedAssetIds);
                    filteredAssets.forEach(a => newSet.delete(String(a.id)));
                    setSelectedAssetIds(newSet);
                  }}
                >
                  Deselect All
                </Button>
              </div>
            </div>

            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {loadingAssets ? (
                <div className="text-center p-3"><Spinner /></div>
              ) : filteredAssets.length > 0 ? (
                <Table size="sm" striped hover>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <Input
                          type="checkbox"
                          checked={filteredAssets.length > 0 && filteredAssets.every(a => selectedAssetIds.has(String(a.id)))}
                          onChange={(e) => {
                            const newSet = new Set(selectedAssetIds);
                            filteredAssets.forEach(a => e.target.checked ? newSet.add(String(a.id)) : newSet.delete(String(a.id)));
                            setSelectedAssetIds(newSet);
                          }}
                        />
                      </th>
                      <th>Asset Name</th>
                      <th>Description</th>
                      <th>Ward Name</th>
                      <th>Location</th>
                      <th>Current Barcode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map(asset => (
                      <tr key={asset.id} onClick={() => {
                        const idStr = String(asset.id);
                        const newSet = new Set(selectedAssetIds);
                        newSet.has(idStr) ? newSet.delete(idStr) : newSet.add(idStr);
                        setSelectedAssetIds(newSet);
                      }} style={{ cursor: 'pointer' }}>
                        <td>
                          <Input
                            type="checkbox"
                            checked={selectedAssetIds.has(String(asset.id))}
                            onChange={() => {}} // Handled by tr onClick
                          />
                        </td>
                        <td>{asset.name}</td>
                        <td className="small text-muted">{asset.description || "-"}</td>
                        <td>{asset.ward_name || "-"}</td>
                        <td className="small">
                          {[asset.building_name, asset.floor, asset.room_name_or_number]
                            .filter(Boolean)
                            .join(" / ") || "-"}
                        </td>
                        <td>{asset.barcode || <Badge color="warning">No Barcode</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
	              ) : (
	                <p className="text-muted text-center p-3">
	                  No assets found matching this institution and category.
	                </p>
	              )}
            </div>
            {selectedAssetIds.size > 0 && (
              <div className="mt-2 text-primary small">
                <strong>{selectedAssetIds.size}</strong> assets selected to apply this naming rule.
              </div>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button type="button" color="light" className="me-2" onClick={toggleModal}>Cancel</Button>
              <Button type="submit" color="primary" disabled={selectedAssetIds.size === 0}>Save Configuration</Button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default BarcodeNamingConfig;
